/**
 * <TutorTeamSection> — « Mon tuteur » ou « Mes tuteurs », sur « Messages » (template).
 *
 * Les tuteurs de l'élève, un par ligne (`<TutorRow>`) : leur capsule, leurs matières, leurs
 * non-lus ; la ligne ouvre SA conversation. Un, deux ou trois tuteurs : autant de lignes dans UNE
 * carte-liste. Aucun tuteur : la section disparaît. L'intitulé suit le nombre (Wajih, 16/10) :
 * « Mon tuteur » avec un seul, « Mes tuteurs » au-delà (`tutorTeamTitle`).
 *
 * Les champions n'y sont pas (Wajih, 15/10) : ils se montrent là où ils agissent (la ligne de
 * relais du prochain live, la mission de Sam, les quêtes), et l'équipe est celle à qui l'élève
 * écrit.
 *
 * `openTutorId` : la conversation de ce tuteur est ouverte à côté (deux volets) — sa ligne est
 * marquée, et sa pastille se retire.
 *
 * Composes DS pieces only — `<List>`, `<CardList>`, `<TutorRow>`.
 */
import React from "react"

import { CardList } from "../../molecules/CardList"
import { List } from "../../molecules/List"
import { TutorRow } from "../../molecules/TutorRow"
import { subjectSummary } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type UITutor } from "../../../ports/primary/ui/types"
import { type UIStudentTutor } from "../../../ports/primary/ui/types/studentTutoring"

export type { UIStudentTutor }

/** L'intitulé de la section : « Mon tuteur » avec un seul tuteur, « Mes tuteurs » avec plusieurs. */
export function tutorTeamTitle(count: number): string {
    return count > 1 ? "Mes tuteurs" : "Mon tuteur"
}

export type TutorTeamSectionProps = {
    tutors: UIStudentTutor[]
    /** Ouvre la conversation avec ce tuteur. ABSENT = les lignes ne pressent pas. */
    onOpenMessages?: (tutor: UITutor) => void
    /** Le tuteur dont la conversation est ouverte à côté. Défaut : aucun. */
    openTutorId?: UITutor["id"] | null
    testID?: string
}

export function TutorTeamSection(props: TutorTeamSectionProps) {
    const { tutors, onOpenMessages, openTutorId = null, testID } = props
    if (tutors.length === 0) return null

    return (
        <List title={tutorTeamTitle(tutors.length)} testID={testID}>
            <CardList>
                {tutors.map(({ tutor, subjects, unreadCount }) => (
                    <TutorRow
                        key={tutor.id}
                        tutor={tutor}
                        caption={subjects != null && subjects.length > 0 ? subjectSummary(subjects) : undefined}
                        unreadCount={onOpenMessages != null ? unreadCount : undefined}
                        onPress={onOpenMessages != null ? () => onOpenMessages(tutor) : undefined}
                        selected={tutor.id === openTutorId}
                    />
                ))}
            </CardList>
        </List>
    )
}
